import { useCallback, useEffect, useState } from 'react';
import { marketingApi, type JourneyCanvas, type MarketingJourney } from '@/api/marketing.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { JOURNEY_TRIGGERS } from './constants';
import { MarketingStatusBadge } from './components/MarketingStatusBadge';
import { MarketingEmptyState } from './components/MarketingEmptyState';
import { JourneyCanvasEditor } from './components/JourneyCanvasEditor';
import {
  canvasToJson,
  createDefaultCanvas,
  parseCanvasJson,
} from './utils/journeyCanvas';
import { notify } from '@/core/toast/notify';

export function MarketingJourneysPage() {
  const [journeys, setJourneys] = useState<MarketingJourney[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('LEAD_CAPTURED');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<MarketingJourney | null>(null);
  const [canvas, setCanvas] = useState<JourneyCanvas | null>(null);
  const [canvasSaving, setCanvasSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setJourneys(await marketingApi.listJourneys());
    } catch (err) {
      showApiError(err, 'Failed to load journeys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectForEdit = (j: MarketingJourney) => {
    setEditing(j);
    const parsed = parseCanvasJson(j.canvasJson);
    setCanvas(parsed ?? createDefaultCanvas(j.triggerEvent));
  };

  const create = async () => {
    setSaving(true);
    try {
      const defaultCanvas = createDefaultCanvas(trigger);
      const created = await marketingApi.createJourney({
        journeyName: name.trim() || 'New journey',
        triggerEvent: trigger,
        canvasJson: canvasToJson(defaultCanvas),
      });
      notify.success('Journey created');
      setName('');
      await load();
      selectForEdit(created);
    } catch (err) {
      showApiError(err, 'Failed to create journey');
    } finally {
      setSaving(false);
    }
  };

  const saveCanvas = async () => {
    if (!editing || !canvas) return;
    setCanvasSaving(true);
    try {
      const updated = await marketingApi.updateJourney(editing.id, {
        canvasJson: canvasToJson(canvas),
      });
      notify.success('Journey flow saved');
      setEditing(updated);
      load();
    } catch (err) {
      showApiError(err, 'Failed to save journey flow');
    } finally {
      setCanvasSaving(false);
    }
  };

  const journeyAction = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      notify.success(msg);
      load();
    } catch (err) {
      showApiError(err, 'Action failed');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Journeys"
        description="Automate nurture flows with triggers, actions, delays, and goals using a visual step builder."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Marketing', to: '/marketing' },
          { label: 'Journeys' },
        ]}
      />

      <section className="card flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[160px] flex-1">
          <Input
            label="Name"
            placeholder="Welcome series"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="min-w-[180px]">
          <Select
            label="Trigger"
            options={JOURNEY_TRIGGERS.map((t) => ({ value: t.value, label: t.label }))}
            value={trigger}
            onChange={setTrigger}
          />
        </div>
        <Button loading={saving} onClick={() => void create()}>
          Create journey
        </Button>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card">
          <h2 className="border-b border-slate-100 p-4 text-sm font-semibold dark:border-slate-800">
            Your journeys
          </h2>
          {loading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : journeys.length === 0 ? (
            <MarketingEmptyState
              title="No journeys"
              description="Create a journey to automate follow-ups after key events."
            />
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {journeys.map((j) => (
                <li key={j.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <button type="button" className="text-left" onClick={() => selectForEdit(j)}>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{j.journeyName}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{j.triggerEvent}</p>
                  </button>
                  <div className="flex items-center gap-2">
                    <MarketingStatusBadge status={j.journeyStatus} kind="journey" />
                    <Button variant="ghost" className="text-xs" onClick={() => selectForEdit(j)}>
                      Edit flow
                    </Button>
                    {j.journeyStatus === 'DRAFT' && (
                      <Button
                        variant="ghost"
                        className="text-xs"
                        onClick={() =>
                          journeyAction(() => marketingApi.submitJourneyApproval(j.id), 'Submitted')
                        }
                      >
                        Submit
                      </Button>
                    )}
                    {j.journeyStatus !== 'ACTIVE' && (
                      <Button
                        variant="ghost"
                        className="text-xs text-emerald-700"
                        onClick={() =>
                          journeyAction(() => marketingApi.activateJourney(j.id), 'Activated')
                        }
                      >
                        Activate
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4">
          {editing && canvas ? (
            <>
              <h2 className="mb-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                {editing.journeyName}
              </h2>
              <p className="mb-4 text-xs text-slate-500">
                Trigger: {editing.triggerEvent} · Status: {editing.journeyStatus}
              </p>
              <JourneyCanvasEditor
                canvas={canvas}
                onChange={setCanvas}
                onSave={() => void saveCanvas()}
                saving={canvasSaving}
                readOnly={editing.journeyStatus === 'ACTIVE'}
                triggerLabel={
                  JOURNEY_TRIGGERS.find((t) => t.value === editing.triggerEvent)?.label ??
                  editing.triggerEvent
                }
              />
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a journey to edit its automation flow.</p>
          )}
        </section>
      </div>
    </div>
  );
}
